import { useState, useEffect, useRef } from 'react';
import { getLanguageName } from '@/utils/languages';
import { useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import { useRoleLabel } from '@/hooks/useRoleLabel';
import { isLastReviewerCancel } from '@/stores/lastReviewerConfirm.store';
import teamApi, { Team, TeamMember } from '@/services/team';
import { formatApiError } from '@/services/api';
import { useAuth } from '@/hooks/useAuth';
import { useUnsavedGuard } from '@/hooks/useUnsavedGuard';
import { useTimezone } from '@/contexts/TimezoneContext';
import { Icon } from '@/components/ui/Icon';
import { Tooltip } from '@/components/ui/Tooltip';
import { Toggle } from '@/components/ui/Toggle';
import { ICONS } from '@/constants/icons';
import { Modal } from '@/components/ui/Modal';
import { ConfirmationModal } from '@/components/ui/ConfirmationModal';
import { cn } from '@/utils/cn';
import { TimezoneSelect } from '@/components/common/TimezoneSelect';
import {
  useTeamInvitations,
  useInviteToTeam,
  useCancelInvitation,
  useResendInvitation,
} from '@/hooks/useInvitations';
import type { Invitation } from '@/types/invitations';
import { useVariationSettings, useUpdateVariationSettings } from '@/hooks/useVariationSettings';
import { useTeamIndustries, useUpdateTeamIndustries } from '@/hooks/useTeamIndustries';
import { INDUSTRY_SUGGESTIONS } from '@/constants/industries';
import { NoBrandSelected } from './BrandScopedTab';
import { useFeatureLock } from '@/hooks/useFeatureLock';
import { isRoleDemotion } from '@/utils/teamRoleRank';
import { getPlanDisplayName } from '@/components/gating/gatingCopy';

/**
 * Helper to get initials from full name
 */
const getInitials = (name: string): string => {
  return name
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

/**
 * Get avatar from member preferences
 */
const getMemberAvatar = (member: TeamMember): string | null => {
  return member.preferences?.avatar || null;
};

/**
 * Get role badge color based on TeamRole enum
 */
const getRoleBadgeColor = (role: string | null | undefined): string => {
  if (!role) return 'bg-gray-100 text-gray-700 border-gray-200';

  switch (role.toUpperCase()) {
    case 'OWNER':
      return 'bg-amber-100 text-amber-700 border-amber-200';
    case 'ADMIN':
      return 'bg-coral-100 text-coral-700 border-coral-200';
    case 'MEMBER':
      return 'bg-primary-100 text-primary-700 border-primary-200';
    case 'REVIEWER':
      return 'bg-green-100 text-green-700 border-green-200';
    default:
      return 'bg-gray-100 text-gray-700 border-gray-200';
  }
};

interface Notification {
  type: 'success' | 'error';
  message: string;
}

/**
 * TeamSettings Component
 *
 * Allows team admins to manage team name and members.
 * Features:
 * - View team details and stats
 * - Edit team name (admin only)
 * - Change member roles (admin only)
 * - Remove members (admin only, cannot remove self)
 */
export const TeamSettings = () => {
  const { t } = useTranslation('settings');
  const roleLabel = useRoleLabel();
  const { user, refreshUser } = useAuth();
  const { formatDateOnly, isPastDate } = useTimezone();
  const location = useLocation();
  const queryClient = useQueryClient();
  const industriesRef = useRef<HTMLDivElement>(null);
  const [notification, setNotification] = useState<Notification | null>(null);

  // Modal states
  const [editModal, setEditModal] = useState(false);
  const [newTeamName, setNewTeamName] = useState('');
  const [newTeamTimezone, setNewTeamTimezone] = useState('');
  const [roleModal, setRoleModal] = useState<{ open: boolean; member: TeamMember | null }>({
    open: false,
    member: null,
  });
  const [selectedRole, setSelectedRole] = useState('');
  // Plan gating: team_roles (role changes, demotions stay possible) and auto_translate (setting is kept, ignored
  // at runtime while locked)
  const rolesLock = useFeatureLock('team_roles');
  const autoTranslateLock = useFeatureLock('auto_translate');
  const [removeModal, setRemoveModal] = useState<{ open: boolean; member: TeamMember | null }>({
    open: false,
    member: null,
  });

  // Invitation modal states
  const [inviteModal, setInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  // Default role depends on workspace type - will be set properly when modal opens
  const [inviteRole, setInviteRole] = useState('');
  const [inviteMessage, setInviteMessage] = useState('');
  const [showMessage, setShowMessage] = useState(false);
  const [industryInput, setIndustryInput] = useState('');
  const [showIndustrySuggestions, setShowIndustrySuggestions] = useState(false);
  const [cancelModal, setCancelModal] = useState<{ open: boolean; invitation: Invitation | null }>({
    open: false,
    invitation: null,
  });

  // 3-dot menu state for mobile member/invitation rows
  const [openMemberMenuId, setOpenMemberMenuId] = useState<number | null>(null);
  const [openInvitationMenuId, setOpenInvitationMenuId] = useState<number | null>(null);

  // Check if user is team admin (OWNER or ADMIN teamRole), super admin, or in God Mode
  // God Mode users (agency staff impersonating client teams) have full admin access
  const isGodMode = !!user?.godModeSession;
  const isAdmin = user?.teamRole === 'OWNER' || user?.teamRole === 'ADMIN' || user?.isSuperAdmin === true || isGodMode;

  // Fetch team data
  const {
    data: team,
    isLoading,
    error,
  } = useQuery<Team>({
    queryKey: ['team'],
    queryFn: teamApi.getTeam,
  });

  // Fetch team invitations
  const teamId = user?.currentTeamId || user?.teamId || 0;
  const {
    data: invitations,
    isLoading: _invitationsLoading,
  } = useTeamInvitations(teamId, { status: 'PENDING' });

  // Fetch variation settings
  const {
    data: variationSettings,
    isLoading: variationSettingsLoading,
  } = useVariationSettings(teamId);

  const updateVariationSettingsMutation = useUpdateVariationSettings();

  // Fetch industry settings
  const {
    data: industrySettings,
    isLoading: industrySettingsLoading,
  } = useTeamIndustries(teamId);

  const updateIndustriesMutation = useUpdateTeamIndustries();

  // Update team mutation
  const updateTeamMutation = useMutation({
    mutationFn: (data: { name?: string; timezone?: string }) => teamApi.updateTeam(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team'] });
      setEditModal(false);
      setNewTeamName('');
      setNewTeamTimezone('');
      setNotification({ type: 'success', message: t('settings_team_success_update') });
    },
    onError: (err: AxiosError<{ error: string }>) => {
      setNotification({
        type: 'error',
        message: err.response?.data?.error || t('settings_team_error_update'),
      });
    },
  });

  // Update member role mutation
  const updateRoleMutation = useMutation({
    mutationFn: ({ memberId, role }: { memberId: number; role: string }) =>
      teamApi.updateMemberRole(memberId, role),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team'] });
      setRoleModal({ open: false, member: null });
      setNotification({ type: 'success', message: t('settings_team_success_role_update') });
    },
    onError: (err: AxiosError<{ error: string }>) => {
      if (isLastReviewerCancel(err)) return;
      setNotification({
        type: 'error',
        message: err.response?.data?.error || t('settings_team_error_role_update'),
      });
    },
  });

  // Remove member mutation
  const removeMemberMutation = useMutation({
    mutationFn: (memberId: number) => teamApi.removeMember(memberId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team'] });
      setRemoveModal({ open: false, member: null });
      setNotification({ type: 'success', message: t('settings_team_success_remove') });
    },
    onError: (err: AxiosError<{ error: string }>) => {
      if (isLastReviewerCancel(err)) return;
      setNotification({
        type: 'error',
        message: err.response?.data?.error || t('settings_team_error_remove'),
      });
    },
  });

  // Invitation mutations
  const inviteMutation = useInviteToTeam();
  const cancelMutation = useCancelInvitation();
  const resendMutation = useResendInvitation();

  // Auto-hide notifications after 5 seconds
  useEffect(() => {
    if (!notification) return;
    const timer = setTimeout(() => setNotification(null), 5000);
    return () => clearTimeout(timer);
  }, [notification]);

  // Scroll to industries section when navigated with #industries hash
  useEffect(() => {
    if (location.hash === '#industries' && industriesRef.current) {
      const timer = setTimeout(() => {
        industriesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [location.hash]);

  // Unsaved-work guard: only dirty while the edit-team modal is open and the
  // name/timezone fields have diverged from the loaded team.
  useUnsavedGuard(
    editModal &&
      (newTeamName.trim() !== (team?.name || '').trim() ||
        newTeamTimezone !== (team?.timezone || 'UTC')),
    'team-settings-edit'
  );

  const handleEditTeam = () => {
    setNewTeamName(team?.name || '');
    setNewTeamTimezone(team?.timezone || 'UTC');
    setEditModal(true);
  };

  const handleChangeRole = (member: TeamMember) => {
    setSelectedRole(member.teamRole || 'MEMBER');
    setRoleModal({ open: true, member });
  };

  const handleInvite = () => {
    if (!inviteEmail.trim()) {
      setNotification({ type: 'error', message: t('settings_team_error_email_required') });
      return;
    }

    inviteMutation.mutate(
      {
        teamId,
        data: {
          email: inviteEmail.trim(),
          role: (rolesLock.locked && !isAgencyClientWorkspace ? 'MEMBER' : inviteRole) as 'ADMIN' | 'MEMBER' | 'REVIEWER',
          message: inviteMessage.trim() || undefined,
        },
      },
      {
        onSuccess: () => {
          setInviteModal(false);
          setInviteEmail('');
          setInviteRole(''); // Reset - will be set properly when modal opens again
          setInviteMessage('');
          setNotification({ type: 'success', message: t('settings_team_success_invite') });
        },
        onError: (err) => {
          setNotification({
            type: 'error',
            message: formatApiError(err).message,
          });
        },
      }
    );
  };

  const handleCancelInvitation = (invitation: Invitation) => {
    setCancelModal({ open: true, invitation });
  };

  const confirmCancelInvitation = () => {
    if (!cancelModal.invitation) return;

    cancelMutation.mutate(
      { invitationId: cancelModal.invitation.id, teamId },
      {
        onSuccess: () => {
          setCancelModal({ open: false, invitation: null });
          setNotification({ type: 'success', message: t('settings_team_success_cancel') });
        },
        onError: (err) => {
          setCancelModal({ open: false, invitation: null });
          setNotification({
            type: 'error',
            message: (err as AxiosError<{ error: string }>)?.response?.data?.error || t('settings_team_error_cancel'),
          });
        },
      }
    );
  };

  const handleResendInvitation = (invitation: Invitation) => {
    resendMutation.mutate(
      { invitationId: invitation.id, teamId },
      {
        onSuccess: () => {
          setNotification({ type: 'success', message: t('settings_team_success_resend') });
        },
        onError: (err) => {
          setNotification({
            type: 'error',
            message: (err as AxiosError<{ error: string }>)?.response?.data?.error || t('settings_team_error_resend'),
          });
        },
      }
    );
  };

  const handleToggleVariations = (enabled: boolean) => {
    updateVariationSettingsMutation.mutate(
      {
        teamId,
        data: { variationsEnabled: enabled },
      },
      {
        onSuccess: () => {
          // Refresh user data so sidebar updates variationsEnabled
          refreshUser();
        },
      }
    );
  };

  const handleToggleAutoTranslate = (enabled: boolean) => {
    updateVariationSettingsMutation.mutate({
      teamId,
      data: { autoTranslateEnabled: enabled },
    });
  };

  const handleToggleLanguage = (languageCode: string) => {
    const currentLanguages = variationSettings?.variationLanguages || [];
    const newLanguages = currentLanguages.includes(languageCode)
      ? currentLanguages.filter(code => code !== languageCode)
      : [...currentLanguages, languageCode];

    updateVariationSettingsMutation.mutate({
      teamId,
      data: { variationLanguages: newLanguages },
    });
  };

  // Industry values are stored in English (feeds trend suggestions); only the display label is localized.
  // Static t() calls so the i18n audit can see every key.
  const industryLabels: Record<string, string> = {
    'Technology': t('settings_industry_technology'),
    'SaaS': t('settings_industry_saas'),
    'E-commerce': t('settings_industry_e_commerce'),
    'Healthcare': t('settings_industry_healthcare'),
    'Finance': t('settings_industry_finance'),
    'Real Estate': t('settings_industry_real_estate'),
    'Education': t('settings_industry_education'),
    'Food & Beverage': t('settings_industry_food_beverage'),
    'Travel & Tourism': t('settings_industry_travel_tourism'),
    'Fashion & Beauty': t('settings_industry_fashion_beauty'),
    'Fitness & Wellness': t('settings_industry_fitness_wellness'),
    'Marketing & Advertising': t('settings_industry_marketing_advertising'),
    'Media & Entertainment': t('settings_industry_media_entertainment'),
    'Non-Profit': t('settings_industry_non_profit'),
    'Automotive': t('settings_industry_automotive'),
    'Construction': t('settings_industry_construction'),
    'Legal': t('settings_industry_legal'),
    'Consulting': t('settings_industry_consulting'),
    'Retail': t('settings_industry_retail'),
    'Manufacturing': t('settings_industry_manufacturing'),
    'Hospitality': t('settings_industry_hospitality'),
    'Agriculture': t('settings_industry_agriculture'),
    'Energy': t('settings_industry_energy'),
    'Telecommunications': t('settings_industry_telecommunications'),
    'Gaming': t('settings_industry_gaming'),
    'Sports': t('settings_industry_sports'),
    'Music': t('settings_industry_music'),
    'Art & Design': t('settings_industry_art_design'),
    'Photography': t('settings_industry_photography'),
    'Coaching': t('settings_industry_coaching'),
  };
  const industryLabel = (industry: string): string => industryLabels[industry] ?? industry;

  const handleAddIndustry = (industry: string) => {
    const trimmed = industry.trim();
    if (!trimmed) return;
    const current = industrySettings?.industries || [];
    if (current.length >= 5) {
      setNotification({ type: 'error', message: t('settings_industries_error_max') });
      return;
    }
    if (current.some(i => i.toLowerCase() === trimmed.toLowerCase())) {
      setNotification({ type: 'error', message: t('settings_industries_error_duplicate') });
      return;
    }
    updateIndustriesMutation.mutate({
      teamId,
      industries: [...current, trimmed],
    });
    setIndustryInput('');
    setShowIndustrySuggestions(false);
  };

  const handleRemoveIndustry = (industry: string) => {
    const current = industrySettings?.industries || [];
    updateIndustriesMutation.mutate({
      teamId,
      industries: current.filter(i => i !== industry),
    });
  };

  const filteredIndustrySuggestions = INDUSTRY_SUGGESTIONS.filter(
    s => (s.toLowerCase().includes(industryInput.toLowerCase()) ||
        industryLabel(s).toLowerCase().includes(industryInput.toLowerCase())) &&
      !(industrySettings?.industries || []).some(i => i.toLowerCase() === s.toLowerCase())
  );

  // Check if this is an agency client workspace (has agencyId or user is in God Mode)
  const isAgencyClientWorkspace = isGodMode || !!team?.agencyId;

  // TeamRole values differ based on workspace type:
  // - Agency brand workspace: Only REVIEWER role (agency staff use God Mode, not team membership)
  // - Regular team: ADMIN, MEMBER roles
  const roles = isAgencyClientWorkspace ? ['REVIEWER'] : ['ADMIN', 'MEMBER'];

  // Language options for variation settings
  const LANGUAGE_OPTIONS = [
    { code: 'EN', label: t('settings_language_en') },
    { code: 'ES', label: t('settings_language_es') },
    { code: 'DE', label: t('settings_language_de') },
    { code: 'FR', label: t('settings_language_fr') },
    { code: 'IT', label: t('settings_language_it') },
    { code: 'PT', label: t('settings_language_pt') },
    { code: 'NL', label: t('settings_language_nl') },
    { code: 'JA', label: t('settings_language_ja') },
    { code: 'ZH', label: t('settings_language_zh') },
    { code: 'AR', label: t('settings_language_ar') },
    { code: 'RU', label: t('settings_language_ru') },
    { code: 'PL', label: t('settings_language_pl') },
    { code: 'KO', label: t('settings_language_ko') },
  ];

  // Email validation helper
  const isValidEmail = (email: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  // Role options for the invite modal
  // Agency brand workspaces: Only REVIEWER role (people who release content)
  // Regular teams: ADMIN/MEMBER roles
  const roleOptions = isAgencyClientWorkspace
    ? [
        { value: 'REVIEWER', label: t('settings_modal_invite_role_reviewer'), icon: ICONS.CHECK, description: t('settings_modal_invite_role_reviewer_desc') },
      ]
    : [
        { value: 'MEMBER', label: t('settings_modal_invite_role_member'), icon: ICONS.USER, description: t('settings_modal_invite_role_member_desc') },
        // team_roles locked: the backend downgrades invites to the default role, so only that role is offered
        ...(rolesLock.locked
          ? []
          : [{ value: 'ADMIN', label: t('settings_modal_invite_role_admin'), icon: ICONS.SHIELD, description: t('settings_modal_invite_role_admin_desc') }]),
      ];

  // Loading state
  if (isLoading) {
    return (
      <div className="py-16 text-center">
        <div className="mx-auto h-10 w-10 animate-spin rounded-full border-4 border-gray-200 border-t-primary-600" />
        <p className="mt-4 font-medium text-gray-600">{t('settings_team_loading')}</p>
      </div>
    );
  }

  // Agency staff who have not entered a brand: team settings belong to a brand
  if (
    (error as AxiosError<{ code?: string }> | null)?.response?.data?.code === 'team.no_context' ||
    (!error && !isLoading && !team)
  ) {
    return <NoBrandSelected />;
  }

  // Error state
  if (error) {
    return (
      <div className="py-16 text-center">
        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-50">
          <Icon name={ICONS.WARNING} size="lg" color="text-red-500" ariaHidden />
        </div>
        <p className="font-medium text-red-600">{t('settings_team_error_title')}</p>
        <p className="mt-1 text-sm text-gray-500">{t('settings_team_error_refresh')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-3 sm:space-y-6">
      {/* Notification Toast */}
      {notification && (
        <div
          className={cn(
            'fixed bottom-[calc(var(--bottom-nav-h)+env(safe-area-inset-bottom)+12px+var(--floating-stack-offset))] lg:bottom-6 right-6 z-50 flex items-center gap-3 rounded-xl px-5 py-4 shadow-2xl',
            notification.type === 'success'
              ? 'bg-green-600 text-white'
              : 'bg-red-600 text-white'
          )}
        >
          <Icon
            name={notification.type === 'success' ? ICONS.CHECK_CIRCLE : ICONS.CROSS_CIRCLE}
            size="sm"
            color="text-white"
            ariaHidden
          />
          <p className="font-medium">{notification.message}</p>
          <button onClick={() => setNotification(null)} className="ml-2 hover:opacity-80">
            <Icon name={ICONS.CROSS} size="sm" color="text-white/80" ariaHidden />
          </button>
        </div>
      )}

      {/* Team Info Card */}
      <div className="relative overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
        <div className="absolute inset-x-0 top-0 h-1 bg-gradient-mint" />
        <div className="p-3 sm:p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[10px] sm:text-sm font-medium text-gray-500 uppercase tracking-wider">
                {t('settings_team_info_name_label')}
              </p>
              <h2 className="text-base sm:text-2xl font-bold text-gray-900 mt-0.5">{team?.name}</h2>
              <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-1">
                <Icon name={ICONS.GLOBE} size="xs" ariaHidden />
                {team?.timezone || 'UTC'}
              </p>
            </div>
            {isAdmin && (
              <button
                onClick={handleEditTeam}
                className="flex items-center gap-1.5 px-2.5 sm:px-4 py-1 sm:py-2 rounded-lg text-xs sm:text-sm font-medium text-primary-600 bg-primary-50 hover:bg-primary-100 transition-colors"
              >
                <Icon name={ICONS.EDIT} size="sm" ariaHidden />
                {t('settings_team_edit_button')}
              </button>
            )}
          </div>

          {/* Stats */}
          <div className="mt-3 sm:mt-6 grid grid-cols-3 gap-1.5 sm:gap-4">
            <div className="p-2 sm:p-4 rounded-xl bg-gray-50 border border-gray-100 text-center">
              <p className="text-lg sm:text-2xl font-bold text-gray-900">{team?.stats.memberCount || 0}</p>
              <p className="text-[10px] sm:text-xs text-gray-500 font-medium">{t('settings_team_stats_members')}</p>
            </div>
            <div className="p-2 sm:p-4 rounded-xl bg-gray-50 border border-gray-100 text-center">
              <p className="text-lg sm:text-2xl font-bold text-gray-900">{team?.stats.channelCount || 0}</p>
              <p className="text-[10px] sm:text-xs text-gray-500 font-medium">{t('settings_team_stats_channels')}</p>
            </div>
            <div className="p-2 sm:p-4 rounded-xl bg-gray-50 border border-gray-100 text-center">
              <p className="text-lg sm:text-2xl font-bold text-gray-900">{team?.stats.postCount || 0}</p>
              <p className="text-[10px] sm:text-xs text-gray-500 font-medium">{t('settings_team_stats_posts')}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Team Members Card */}
      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
        <div className="border-b border-gray-100 bg-gray-50/50 px-3 sm:px-6 py-2.5 sm:py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h2 className="text-sm sm:text-lg font-semibold text-gray-900">{t('settings_team_members_title')}</h2>
            <span className="rounded-full bg-primary-100 px-2 py-0.5 text-xs font-semibold text-primary-700">
              {team?.members.length || 0}
            </span>
          </div>
          {isAdmin && (
            <button
              onClick={() => {
                // Set default role based on workspace type
                setInviteRole(isAgencyClientWorkspace ? 'REVIEWER' : 'MEMBER');
                setInviteModal(true);
              }}
              className="flex items-center gap-1.5 w-8 h-8 sm:w-auto sm:h-auto justify-center px-0 sm:px-4 py-0 sm:py-2 rounded-lg text-xs sm:text-sm font-medium text-primary-600-contrast bg-primary-600 hover:bg-primary-700 transition-colors"
              aria-label={t('settings_team_invite_button')}
            >
              <Icon name={ICONS.PLUS} size="sm" ariaHidden />
              <span className="hidden sm:inline">{t('settings_team_invite_button')}</span>
            </button>
          )}
        </div>

        <div className="divide-y divide-gray-100">
          {team?.members.map((member) => (
            <div
              key={member.id}
              className="px-3 sm:px-6 py-2.5 sm:py-4 hover:bg-gray-50/50 transition-colors"
            >
              {/* Mobile layout */}
              <div className="flex items-center gap-2.5 sm:hidden">
                {/* Avatar */}
                {getMemberAvatar(member) ? (
                  <img
                    src={getMemberAvatar(member)!}
                    alt={member.fullName}
                    className="h-8 w-8 rounded-full object-cover flex-shrink-0"
                  />
                ) : (
                  <div className="h-8 w-8 rounded-full bg-primary-400 flex items-center justify-center text-primary-400-contrast font-bold text-xs flex-shrink-0">
                    {getInitials(member.fullName)}
                  </div>
                )}

                {/* Name + email take remaining space */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <p className="text-xs font-semibold text-gray-900 truncate">
                      {member.fullName}
                    </p>
                    {member.id === user?.id && (
                      <span className="text-[10px] font-normal text-gray-400">{t('settings_team_member_you')}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <p className="text-[11px] text-gray-500 truncate">{member.email}</p>
                  </div>
                  {/* Role badge on second row */}
                  <div className="mt-1">
                    <span
                      className={cn(
                        'inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium border',
                        getRoleBadgeColor(member.teamRole)
                      )}
                    >
                      {roleLabel(member.teamRole)}
                    </span>
                  </div>
                </div>

                {/* 3-dot menu — admin only, not self */}
                {isAdmin && member.id !== user?.id && (
                  <div className="relative flex-shrink-0">
                    <button
                      onClick={() => setOpenMemberMenuId(openMemberMenuId === member.id ? null : member.id)}
                      className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                      aria-label={t('settings_team_member_actions')}
                    >
                      <Icon name={ICONS.ELLIPSIS_VERTICAL} size="sm" ariaHidden />
                    </button>
                    {openMemberMenuId === member.id && (
                      <div className="absolute right-0 top-full mt-1 w-44 rounded-xl border border-gray-200 bg-white shadow-lg z-20">
                        <button
                          onClick={() => { setOpenMemberMenuId(null); handleChangeRole(member); }}
                          className="flex items-center gap-2 w-full px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-50 rounded-t-xl transition-colors"
                        >
                          <Icon name={ICONS.SETTINGS} size="sm" color="text-gray-400" ariaHidden />
                          {t('settings_team_change_role')}
                        </button>
                        <button
                          onClick={() => { setOpenMemberMenuId(null); setRemoveModal({ open: true, member }); }}
                          className="flex items-center gap-2 w-full px-3 py-2.5 text-sm text-red-600 hover:bg-red-50 rounded-b-xl transition-colors"
                        >
                          <Icon name={ICONS.TRASH} size="sm" color="text-red-500" ariaHidden />
                          {t('settings_team_remove_from_team')}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Desktop layout — single row, unchanged */}
              <div className="hidden sm:flex items-center justify-between">
                <div className="flex items-center gap-4 min-w-0">
                  {getMemberAvatar(member) ? (
                    <img
                      src={getMemberAvatar(member)!}
                      alt={member.fullName}
                      className="h-10 w-10 rounded-full object-cover flex-shrink-0"
                    />
                  ) : (
                    <div className="h-10 w-10 rounded-full bg-primary-400 flex items-center justify-center text-primary-400-contrast font-bold text-xs flex-shrink-0">
                      {getInitials(member.fullName)}
                    </div>
                  )}
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">
                      {member.fullName}
                      {member.id === user?.id && (
                        <span className="ml-1.5 text-xs font-normal text-gray-400">{t('settings_team_member_you')}</span>
                      )}
                    </p>
                    <p className="text-xs text-gray-500 truncate">{member.email}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3 flex-shrink-0">
                  <span
                    className={cn(
                      'inline-flex items-center px-3 py-0.5 rounded-full text-xs font-medium border',
                      getRoleBadgeColor(member.teamRole)
                    )}
                  >
                    {roleLabel(member.teamRole)}
                  </span>

                  {isAdmin && member.id !== user?.id && (
                    <div className="flex items-center gap-1">
                      <Tooltip content={t('settings_team_change_role')}>
                        <button
                          onClick={() => handleChangeRole(member)}
                          className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                        >
                          <Icon name={ICONS.SETTINGS} size="sm" ariaHidden />
                        </button>
                      </Tooltip>
                      <Tooltip content={t('settings_team_remove_from_team')}>
                        <button
                          onClick={() => setRemoveModal({ open: true, member })}
                          className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                        >
                          <Icon name={ICONS.TRASH} size="sm" ariaHidden />
                        </button>
                      </Tooltip>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Pending Invitations Card */}
      {isAdmin && invitations && invitations.length > 0 && (
        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
          <div className="border-b border-gray-100 bg-gray-50/50 px-3 sm:px-6 py-2.5 sm:py-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <h2 className="text-sm sm:text-lg font-semibold text-gray-900">{t('settings_team_invitations_title')}</h2>
              <span className="rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-semibold text-yellow-700">
                {invitations.length}
              </span>
            </div>
          </div>

          <div className="divide-y divide-gray-100">
            {invitations.map((invitation) => {
              const isExpired = isPastDate(invitation.expiresAt);
              return (
                <div
                  key={invitation.id}
                  className="px-3 sm:px-6 py-2.5 sm:py-4 hover:bg-gray-50/50 transition-colors"
                >
                  {/* Mobile layout */}
                  <div className="flex items-center gap-2.5 sm:hidden">
                    {/* Mail icon */}
                    <div className="h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                      <Icon name={ICONS.MAIL} size="sm" color="text-gray-600" ariaHidden />
                    </div>

                    {/* Email + date take remaining space */}
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-gray-900 truncate">{invitation.email}</p>
                      <p className="text-[11px] text-gray-500 truncate">
                        {t('settings_team_invited_date', { date: formatDateOnly(invitation.createdAt) })}
                      </p>
                      {/* Status + role badges on second row */}
                      <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                        <span
                          className={cn(
                            'inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium border',
                            isExpired
                              ? 'bg-red-100 text-red-700 border-red-200'
                              : 'bg-yellow-100 text-yellow-700 border-yellow-200'
                          )}
                        >
                          {isExpired ? t('settings_team_invitation_expired') : t('settings_team_invitation_pending')}
                        </span>
                        <span
                          className={cn(
                            'inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium border',
                            getRoleBadgeColor(invitation.teamRole || '')
                          )}
                        >
                          {roleLabel(invitation.teamRole)}
                        </span>
                      </div>
                    </div>

                    {/* 3-dot menu */}
                    <div className="relative flex-shrink-0">
                      <button
                        onClick={() => setOpenInvitationMenuId(openInvitationMenuId === invitation.id ? null : invitation.id)}
                        className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                        aria-label={t('settings_team_invitation_actions')}
                      >
                        <Icon name={ICONS.ELLIPSIS_VERTICAL} size="sm" ariaHidden />
                      </button>
                      {openInvitationMenuId === invitation.id && (
                        <div className="absolute right-0 top-full mt-1 w-44 rounded-xl border border-gray-200 bg-white shadow-lg z-20">
                          <button
                            onClick={() => { setOpenInvitationMenuId(null); handleResendInvitation(invitation); }}
                            disabled={resendMutation.isPending}
                            className="flex items-center gap-2 w-full px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-50 rounded-t-xl transition-colors disabled:opacity-50"
                          >
                            <Icon name={ICONS.REFRESH} size="sm" color="text-gray-400" ariaHidden />
                            {t('settings_team_resend_invitation')}
                          </button>
                          <button
                            onClick={() => { setOpenInvitationMenuId(null); handleCancelInvitation(invitation); }}
                            disabled={cancelMutation.isPending}
                            className="flex items-center gap-2 w-full px-3 py-2.5 text-sm text-red-600 hover:bg-red-50 rounded-b-xl transition-colors disabled:opacity-50"
                          >
                            <Icon name={ICONS.CROSS} size="sm" color="text-red-500" ariaHidden />
                            {t('settings_team_cancel_invitation')}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Desktop layout — single row, unchanged */}
                  <div className="hidden sm:flex items-center justify-between">
                    <div className="flex items-center gap-4 min-w-0">
                      <div className="h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                        <Icon name={ICONS.MAIL} size="sm" color="text-gray-600" ariaHidden />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-900 truncate">{invitation.email}</p>
                        <p className="text-xs text-gray-500">
                          {t('settings_team_invited_date', { date: formatDateOnly(invitation.createdAt) })}
                          {invitation.message && (
                            <span className="ml-2 text-gray-400">• {invitation.message}</span>
                          )}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 flex-shrink-0">
                      <span
                        className={cn(
                          'inline-flex items-center px-3 py-0.5 rounded-full text-xs font-medium border',
                          getRoleBadgeColor(invitation.teamRole || '')
                        )}
                      >
                        {roleLabel(invitation.teamRole)}
                      </span>

                      <span
                        className={cn(
                          'inline-flex items-center px-3 py-0.5 rounded-full text-xs font-medium border',
                          isExpired
                            ? 'bg-red-100 text-red-700 border-red-200'
                            : 'bg-yellow-100 text-yellow-700 border-yellow-200'
                        )}
                      >
                        {isExpired ? t('settings_team_invitation_expired') : t('settings_team_invitation_pending')}
                      </span>

                      <div className="flex items-center gap-1">
                        <Tooltip content={t('settings_team_resend_invitation')}>
                          <button
                            onClick={() => handleResendInvitation(invitation)}
                            disabled={resendMutation.isPending}
                            className="p-1.5 rounded-lg text-gray-400 hover:text-primary-600 hover:bg-primary-50 transition-colors disabled:opacity-50"
                          >
                            <Icon name={ICONS.REFRESH} size="sm" ariaHidden />
                          </button>
                        </Tooltip>
                        <Tooltip content={t('settings_team_cancel_invitation')}>
                          <button
                            onClick={() => handleCancelInvitation(invitation)}
                            disabled={cancelMutation.isPending}
                            className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50"
                          >
                            <Icon name={ICONS.CROSS} size="sm" ariaHidden />
                          </button>
                        </Tooltip>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Variations Settings Card (Admin Only) */}
      {isAdmin && (
        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
          <div className="border-b border-gray-100 bg-gray-50/50 px-3 sm:px-6 py-2.5 sm:py-4">
            <div className="flex items-center gap-2">
              <h2 className="text-sm sm:text-lg font-semibold text-gray-900">{t('settings_variations_title', { defaultValue: 'Content Variations' })}</h2>
            </div>
          </div>

          <div className="p-3 sm:p-6 space-y-3 sm:space-y-6">
            {variationSettingsLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-primary-600" />
              </div>
            ) : (
              <>
                {/* Enable Variations Toggle */}
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="text-sm sm:text-base font-semibold text-gray-900">{t('settings_variations_enable', { defaultValue: 'Enable Variations' })}</h3>
                    <p className="text-xs sm:text-sm text-gray-500 mt-0.5">
                      {t('settings_variations_enable_desc', { defaultValue: 'Create language and custom variations of your posts' })}
                    </p>
                  </div>
                  <Toggle
                    ariaLabel={t('settings_variations_enable', { defaultValue: 'Enable Variations' })}
                    checked={variationSettings?.variationsEnabled || false}
                    onChange={handleToggleVariations}
                  />
                </div>

                {/* Variation Languages (only show if variations enabled) */}
                {variationSettings?.variationsEnabled && (
                  <>
                    <div className="border-t border-gray-100 pt-4 sm:pt-6">
                      <h3 className="text-sm sm:text-base font-semibold text-gray-900 mb-2">
                        {t('settings_variations_languages', { defaultValue: 'Variation Languages' })}
                      </h3>
                      <p className="text-xs sm:text-sm text-gray-500 mb-3">
                        {t('settings_variations_languages_desc', { defaultValue: 'Select languages for automatic variation creation' })}
                      </p>

                      {/* Language chips */}
                      <div className="flex flex-wrap gap-1.5 sm:gap-2">
                        {LANGUAGE_OPTIONS.map((lang) => {
                          const isSelected = variationSettings?.variationLanguages?.includes(lang.code) || false;
                          return (
                            <button
                              key={lang.code}
                              onClick={() => handleToggleLanguage(lang.code)}
                              className={cn(
                                'px-2.5 sm:px-4 py-1 sm:py-2 rounded-lg text-xs sm:text-sm font-medium border-2 transition-all',
                                isSelected
                                  ? 'bg-primary-50 border-primary-500 text-primary-700'
                                  : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                              )}
                            >
                              {isSelected && (
                                <Icon name={ICONS.CHECK} size="xs" className="inline mr-1" ariaHidden />
                              )}
                              {lang.code} - {getLanguageName(lang.code)}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Auto-translate Toggle */}
                    <div className="border-t border-gray-100 pt-4 sm:pt-6">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="text-sm sm:text-base font-semibold text-gray-900">{t('settings_variations_auto_translate', { defaultValue: 'Auto-translate' })}</h3>
                          <p className="text-xs sm:text-sm text-gray-500 mt-0.5">
                            {t('settings_variations_auto_translate_desc', { defaultValue: 'Automatically create translated variations when saving posts (uses AI tokens)' })}
                          </p>
                          {autoTranslateLock.locked && (
                            <button
                              type="button"
                              onClick={autoTranslateLock.showUpsell}
                              className="mt-1.5 inline-flex items-center gap-1.5 text-xs font-medium text-primary-700 hover:text-primary-800"
                            >
                              <Icon name={ICONS.LOCK} size="xs" ariaHidden />
                              {autoTranslateLock.label}
                            </button>
                          )}
                        </div>
                        <Toggle
                          ariaLabel={t('settings_variations_auto_translate', { defaultValue: 'Auto-translate' })}
                          checked={variationSettings?.autoTranslateEnabled || false}
                          onChange={handleToggleAutoTranslate}
                          disabled={autoTranslateLock.locked}
                        />
                      </div>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Industries Settings Card (Admin Only) */}
      {isAdmin && (
        <div ref={industriesRef} className="rounded-2xl border border-gray-200 bg-white shadow-sm">
          <div className="border-b border-gray-100 bg-gray-50/50 px-3 sm:px-6 py-2.5 sm:py-4">
            <div className="flex items-center gap-2">
              <h2 className="text-sm sm:text-lg font-semibold text-gray-900">{t('settings_industries_title')}</h2>
            </div>
          </div>

          <div className="p-3 sm:p-6 space-y-3 sm:space-y-4">
            {industrySettingsLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-primary-600" />
              </div>
            ) : (
              <>
                <p className="text-xs sm:text-sm text-gray-500">
                  {t('settings_industries_desc')}
                </p>

                {/* Current industries as chips */}
                <div className="flex flex-wrap gap-1.5 sm:gap-2">
                  {(industrySettings?.industries || []).map((industry) => (
                    <span
                      key={industry}
                      className="inline-flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-0.5 sm:py-1.5 rounded-md sm:rounded-lg bg-primary-50 text-primary-700 text-xs sm:text-sm font-medium border border-primary-200"
                    >
                      {industryLabel(industry)}
                      <button
                        onClick={() => handleRemoveIndustry(industry)}
                        className="hover:text-primary-900 transition-colors"
                        aria-label={t('settings_industries_remove_aria', { industry: industryLabel(industry) })}
                      >
                        <Icon name={ICONS.CROSS} size="xs" ariaHidden />
                      </button>
                    </span>
                  ))}
                </div>

                {/* Add industry input */}
                {(industrySettings?.industries || []).length < 5 && (
                  <div className="relative">
                    <input
                      type="text"
                      value={industryInput}
                      onChange={(e) => {
                        setIndustryInput(e.target.value);
                        setShowIndustrySuggestions(true);
                      }}
                      onFocus={() => setShowIndustrySuggestions(true)}
                      onBlur={() => {
                        // Delay to allow click events on suggestions
                        setTimeout(() => setShowIndustrySuggestions(false), 200);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && industryInput.trim()) {
                          e.preventDefault();
                          handleAddIndustry(industryInput);
                        }
                      }}
                      placeholder={t('settings_industries_search_placeholder')}
                      className="w-full px-3 h-[34px] text-xs lg:h-auto lg:px-4 lg:py-2.5 lg:text-sm rounded-lg border border-gray-300 ring-1 ring-transparent focus:outline-none focus:ring-primary-400 focus:border-primary-400 transition-colors"
                      maxLength={100}
                    />

                    {/* Suggestions dropdown */}
                    {showIndustrySuggestions && filteredIndustrySuggestions.length > 0 && (
                      <div className="absolute z-10 mt-1 w-full max-h-48 overflow-auto rounded-lg border border-gray-200 bg-white shadow-lg">
                        {filteredIndustrySuggestions.slice(0, 8).map((suggestion) => (
                          <button
                            key={suggestion}
                            type="button"
                            onClick={() => handleAddIndustry(suggestion)}
                            className="w-full text-left px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm hover:bg-primary-50 hover:text-primary-700 transition-colors"
                          >
                            {industryLabel(suggestion)}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {(industrySettings?.industries || []).length >= 5 && (
                  <p className="text-xs text-gray-400">
                    {t('settings_industries_max_reached')}
                  </p>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Invite Member Modal */}
      <Modal
        open={inviteModal}
        onOpenChange={(open) => {
          setInviteModal(open);
          if (!open) {
            setInviteEmail('');
            setInviteRole(''); // Reset - will be set properly when modal opens again
            setInviteMessage('');
            setShowMessage(false);
          }
        }}
        title="" // Custom header inside
        size="md"
        footer={null} // Custom footer inside
      >
        <div className="space-y-6">
          {/* Hero Header */}
          <div className="text-center pb-2">
            <div className="mx-auto w-16 h-16 rounded-full bg-primary-400 flex items-center justify-center mb-4">
              <Icon name={ICONS.USER} size="lg" color="text-white" ariaHidden />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              {t('settings_modal_invite_title')}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {t('settings_modal_invite_subtitle')}
            </p>
          </div>

          {/* Email Input with validation */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('settings_modal_invite_email_label')}
            </label>
            <div className="relative">
              <input
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder={t('settings_modal_invite_email_placeholder')}
                className="w-full px-4 py-3 pr-10 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none text-sm transition-all"
                autoFocus
              />
              {/* Validation icon */}
              {inviteEmail && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  {isValidEmail(inviteEmail) ? (
                    <Icon name={ICONS.CHECK_CIRCLE} size="sm" color="text-green-500" ariaHidden />
                  ) : (
                    <Icon name={ICONS.CROSS_CIRCLE} size="sm" color="text-red-400" ariaHidden />
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Role Selection Cards */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              {t('settings_modal_invite_role_label')}
            </label>
            <div className="grid grid-cols-2 gap-3">
              {/* Render role cards */}
              {roleOptions.map((role) => (
                <button
                  key={role.value}
                  type="button"
                  onClick={() => setInviteRole(role.value)}
                  className={cn(
                    'p-4 rounded-xl border-2 text-left transition-all',
                    inviteRole === role.value
                      ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                  )}
                >
                  <div className="flex items-center gap-3 mb-1">
                    <Icon
                      name={role.icon}
                      size="sm"
                      color={inviteRole === role.value ? 'text-primary-600' : 'text-gray-400'}
                      ariaHidden
                    />
                    <span className={cn(
                      'font-medium',
                      inviteRole === role.value ? 'text-primary-700 dark:text-primary-400' : 'text-gray-700 dark:text-gray-300'
                    )}>
                      {role.label}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 ml-7">
                    {role.description}
                  </p>
                </button>
              ))}
            </div>
            {rolesLock.locked && !isAgencyClientWorkspace && (
              <p className="mt-2 flex items-center gap-1.5 text-xs text-gray-500" data-testid="invite-roles-locked-hint">
                <Icon name={ICONS.LOCK} size="xs" ariaHidden />
                {t('common:gating_team_roles_invite_hint')}
              </p>
            )}
          </div>

          {/* Collapsible Message */}
          <div>
            <button
              type="button"
              onClick={() => setShowMessage(!showMessage)}
              className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 transition-colors"
            >
              <Icon name={showMessage ? ICONS.CARET_DOWN : ICONS.CARET_RIGHT} size="xs" ariaHidden />
              {t('settings_team_add_message_toggle')}
            </button>
            {showMessage && (
              <div className="mt-3">
                <textarea
                  value={inviteMessage}
                  onChange={(e) => setInviteMessage(e.target.value)}
                  placeholder={t('settings_team_invite_message_placeholder')}
                  rows={3}
                  maxLength={500}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none text-sm resize-none transition-all"
                />
                <p className="mt-1 text-xs text-gray-400 text-right">
                  {inviteMessage.length}/500
                </p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 pt-2 border-t border-gray-100 dark:border-gray-800">
            <button
              onClick={() => {
                setInviteModal(false);
                setInviteEmail('');
                setInviteRole(''); // Reset - will be set properly when modal opens again
                setInviteMessage('');
                setShowMessage(false);
              }}
              disabled={inviteMutation.isPending}
              className="px-5 py-2.5 rounded-xl text-sm font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50 transition-colors"
            >
              {t('settings_modal_cancel')}
            </button>
            <button
              onClick={handleInvite}
              disabled={inviteMutation.isPending || !inviteEmail.trim() || !isValidEmail(inviteEmail)}
              className="px-5 py-2.5 rounded-xl text-sm font-medium text-primary-400-contrast bg-primary-400 hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 shadow-lg shadow-primary-500/25 transition-all"
            >
              {inviteMutation.isPending ? (
                <>
                  <Icon name={ICONS.LOADER} size="sm" className="animate-spin" ariaHidden />
                  {t('settings_modal_invite_sending')}
                </>
              ) : (
                <>
                  {t('settings_modal_invite_send')}
                  <Icon name={ICONS.ARROW_RIGHT} size="sm" ariaHidden />
                </>
              )}
            </button>
          </div>
        </div>
      </Modal>

      {/* Edit Team Modal */}
      <Modal
        open={editModal}
        onOpenChange={setEditModal}
        title={t('settings_modal_edit_team_title')}
        size="sm"
        footer={
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setEditModal(false)}
              disabled={updateTeamMutation.isPending}
              className="px-4 py-2 rounded-lg text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 disabled:opacity-50"
            >
              {t('settings_modal_cancel')}
            </button>
            <button
              onClick={() => updateTeamMutation.mutate({ name: newTeamName, timezone: newTeamTimezone })}
              disabled={updateTeamMutation.isPending || !newTeamName.trim()}
              className="px-4 py-2 rounded-lg text-sm font-medium text-primary-600-contrast bg-primary-600 hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2"
            >
              {updateTeamMutation.isPending && (
                <Icon name={ICONS.LOADER} size="sm" className="animate-spin" ariaHidden />
              )}
              {t('settings_modal_save')}
            </button>
          </div>
        }
      >
        <div className="space-y-4">
          <div>
            <label htmlFor="team-name" className="block text-sm font-medium text-gray-700 mb-1">
              {t('settings_team_info_name_label')}
            </label>
            <input
              id="team-name"
              type="text"
              value={newTeamName}
              onChange={(e) => setNewTeamName(e.target.value)}
              placeholder={t('settings_modal_edit_team_name_placeholder')}
              maxLength={100}
              className="w-full px-4 py-2.5 rounded-lg border border-gray-200 focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none text-sm"
              autoFocus
            />
          </div>
          <TimezoneSelect
            value={newTeamTimezone}
            onChange={setNewTeamTimezone}
            label={t('settings_modal_edit_team_tz_label')}
            showCurrentTime={true}
          />
        </div>
      </Modal>

      {/* Change Role Modal */}
      <Modal
        open={roleModal.open}
        onOpenChange={(open) => setRoleModal({ open, member: open ? roleModal.member : null })}
        title={t('settings_modal_role_title')}
        size="sm"
        footer={
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setRoleModal({ open: false, member: null })}
              disabled={updateRoleMutation.isPending}
              className="px-4 py-2 rounded-lg text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 disabled:opacity-50"
            >
              {t('settings_modal_cancel')}
            </button>
            <button
              onClick={() =>
                roleModal.member &&
                updateRoleMutation.mutate({ memberId: roleModal.member.id, role: selectedRole })
              }
              disabled={
                updateRoleMutation.isPending || selectedRole === roleModal.member?.teamRole
              }
              className="px-4 py-2 rounded-lg text-sm font-medium text-primary-600-contrast bg-primary-600 hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2"
            >
              {updateRoleMutation.isPending && (
                <Icon name={ICONS.LOADER} size="sm" className="animate-spin" ariaHidden />
              )}
              {t('settings_modal_role_confirm')}
            </button>
          </div>
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            {t('settings_modal_role_description', { name: roleModal.member?.fullName })}
          </p>
          <div className="space-y-2">
            {roles.map((role) => {
              // team_roles locked: only the member's current role and lower roles (demotions) stay selectable
              const roleLocked =
                rolesLock.locked &&
                role !== roleModal.member?.teamRole &&
                !isRoleDemotion(roleModal.member?.teamRole, role);
              return (
                <label
                  key={role}
                  className={cn(
                    'flex items-center gap-3 p-3 rounded-lg border transition-colors',
                    roleLocked ? 'cursor-not-allowed opacity-60 border-gray-200' : 'cursor-pointer',
                    !roleLocked && selectedRole === role
                      ? 'border-primary-500 bg-primary-50'
                      : !roleLocked && 'border-gray-200 hover:bg-gray-50'
                  )}
                >
                  <input
                    type="radio"
                    name="role"
                    value={role}
                    checked={selectedRole === role}
                    disabled={roleLocked}
                    onChange={(e) => setSelectedRole(e.target.value)}
                    className="text-primary-600 focus:ring-primary-500"
                  />
                  <span className="font-medium text-gray-900">{roleLabel(role)}</span>
                  {roleLocked && <Icon name={ICONS.LOCK} size="xs" color="text-gray-500" className="ml-auto" ariaHidden />}
                </label>
              );
            })}
            {rolesLock.locked && (
              <button
                type="button"
                onClick={rolesLock.showUpsell}
                className="flex items-start gap-1.5 text-left text-xs text-primary-700 hover:text-primary-800"
              >
                <Icon name={ICONS.LOCK} size="xs" className="mt-0.5" ariaHidden />
                <span>
                  {rolesLock.upsellPlanSlug && getPlanDisplayName(rolesLock.upsellPlanSlug)
                    ? t('common:gating_roles_locked_note', { plan: getPlanDisplayName(rolesLock.upsellPlanSlug) })
                    : t('common:gating_roles_locked_note_generic')}
                </span>
              </button>
            )}
          </div>
        </div>
      </Modal>

      {/* Remove Member Confirmation */}
      <ConfirmationModal
        open={removeModal.open}
        onOpenChange={(open) => setRemoveModal({ open, member: open ? removeModal.member : null })}
        title={t('settings_modal_remove_title')}
        description={t('settings_modal_remove_description', { name: removeModal.member?.fullName })}
        variant="danger"
        confirmText={t('settings_modal_remove_confirm')}
        cancelText={t('settings_modal_cancel')}
        onConfirm={() => {
          if (removeModal.member) {
            removeMemberMutation.mutate(removeModal.member.id);
          }
        }}
        isLoading={removeMemberMutation.isPending}
      />

      {/* Cancel Invitation Confirmation */}
      <ConfirmationModal
        open={cancelModal.open}
        onOpenChange={(open) => setCancelModal({ open, invitation: open ? cancelModal.invitation : null })}
        title={t('settings_modal_cancel_invitation_title', { defaultValue: 'Cancel Invitation' })}
        description={t('settings_modal_cancel_invitation_description', { email: cancelModal.invitation?.email, defaultValue: `Are you sure you want to cancel the invitation to ${cancelModal.invitation?.email}?` })}
        variant="warning"
        confirmText={t('settings_modal_cancel_invitation_confirm', { defaultValue: 'Cancel Invitation' })}
        cancelText={t('settings_modal_keep', { defaultValue: 'Keep' })}
        onConfirm={confirmCancelInvitation}
        isLoading={cancelMutation.isPending}
      />
    </div>
  );
};
